import { CURRENCY_CONFIGS, DEFAULT_CURRENCY_CONFIG, DEFAULT_FALLBACK, HAIR_SPACE } from '../currency-helpers.constants';
import type { CurrencyInput, FormatCurrencyOptions } from '../currency-helpers.type';
import { safeParseCurrencyInput } from '../parsing/parsing';

/**
 * Core currency formatting function following Bloomfilter's standardized USD formatting requirements.
 *
 * Format specifications:
 * - Currency symbol: Always use the dollar sign ($)
 * - Hair space: Insert a Unicode HAIR SPACE (U+200A) between the dollar sign and the numeric value
 * - Thousands separator: Use commas to separate thousands (e.g., 104,325, not 104325)
 * - Decimal places: Always show two decimal digits, even for round numbers (e.g., $1,000.00)
 * - Negative values: Display with a minus sign before the symbol (e.g., -$1,250.75)
 * - Zero values: Format as $0.00
 *
 * Examples:
 * - formatCurrency(5) → "$ 5.00"
 * - formatCurrency(1200) → "$ 1,200.00"
 * - formatCurrency(10000.5) → "$ 10,000.50"
 * - formatCurrency(-3745.99) → "-$ 3,745.99"
 * - formatCurrency(0) → "$ 0.00"
 *
 * @param {CurrencyInput} value - The value to format (number, string, null, or undefined)
 * @param {FormatCurrencyOptions} [options] - Formatting options
 * @returns {string} Formatted currency string
 */
const formatCurrency = (value: CurrencyInput, options?: FormatCurrencyOptions): string => {
  const {
    currency = 'USD',
    locale,
    showSymbol = true,
    minimumFractionDigits,
    maximumFractionDigits,
    fallback = DEFAULT_FALLBACK,
    useHairSpace,
    useAccountingFormat = false,
  } = options || {};

  // Parse the input value
  const parsedValue = safeParseCurrencyInput(value);

  // If parsing fails, return fallback
  if (parsedValue === null) {
    return fallback;
  }

  // Get currency configuration
  const config = CURRENCY_CONFIGS[currency] || DEFAULT_CURRENCY_CONFIG;

  // Determine final options
  const finalLocale = locale || config.locale;
  const finalMinFractionDigits = minimumFractionDigits ?? config.minimumFractionDigits;
  const finalMaxFractionDigits = maximumFractionDigits ?? config.maximumFractionDigits;
  const finalUseHairSpace = useHairSpace ?? config.useHairSpace;

  try {
    // Handle negative values for accounting format
    const isNegative = parsedValue < 0;
    const absoluteValue = Math.abs(parsedValue);

    // Format the number using Intl.NumberFormat
    const formatter = new Intl.NumberFormat(finalLocale, {
      style: 'decimal',
      minimumFractionDigits: finalMinFractionDigits,
      maximumFractionDigits: finalMaxFractionDigits,
      useGrouping: true,
    });

    const formattedNumber = formatter.format(absoluteValue);

    // Build the final formatted string
    if (!showSymbol) {
      return isNegative ? `-${formattedNumber}` : formattedNumber;
    }

    // Add currency symbol with proper spacing
    const space = finalUseHairSpace ? HAIR_SPACE : '';
    const symbol = config.symbol;

    if (useAccountingFormat && isNegative) {
      // Accounting format: ($1,234.56)
      return config.placement === 'prefix'
        ? `(${symbol}${space}${formattedNumber})`
        : `(${formattedNumber}${space}${symbol})`;
    }

    // Standard format
    const prefix = isNegative ? '-' : '';

    if (config.placement === 'prefix') {
      return `${prefix}${symbol}${space}${formattedNumber}`;
    }

    return `${prefix}${formattedNumber}${space}${symbol}`;
  } catch (error) {
    console.warn('Error formatting currency:', error);
    return fallback;
  }
};

/**
 * Formats currency with compact notation for large numbers (e.g., $1.2K, $3.5M, $1.2B)
 *
 * @param {CurrencyInput} value - The value to format
 * @param {FormatCurrencyOptions} [options] - Formatting options
 * @returns {string} Formatted currency string with compact notation
 */
const formatCurrencyCompact = (value: CurrencyInput, options?: FormatCurrencyOptions): string => {
  const { currency = 'USD', locale, fallback = DEFAULT_FALLBACK, useHairSpace } = options || {};

  const parsedValue = safeParseCurrencyInput(value);

  if (parsedValue === null) {
    return fallback;
  }

  const config = CURRENCY_CONFIGS[currency] || DEFAULT_CURRENCY_CONFIG;
  const finalLocale = locale || config.locale;
  const finalUseHairSpace = useHairSpace ?? config.useHairSpace;

  try {
    const isNegative = parsedValue < 0;
    const absoluteValue = Math.abs(parsedValue);

    const formatter = new Intl.NumberFormat(finalLocale, {
      style: 'decimal',
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });

    const formattedNumber = formatter.format(absoluteValue);
    const space = finalUseHairSpace ? HAIR_SPACE : '';
    const symbol = config.symbol;
    const prefix = isNegative ? '-' : '';

    if (config.placement === 'prefix') {
      return `${prefix}${symbol}${space}${formattedNumber}`;
    }

    return `${prefix}${formattedNumber}${space}${symbol}`;
  } catch (error) {
    console.warn('Error formatting currency (compact):', error);
    return fallback;
  }
};

/**
 * Formats a currency value without the currency symbol (numbers only)
 *
 * @param {CurrencyInput} value - The value to format
 * @param {Omit<FormatCurrencyOptions, 'showSymbol' | 'useHairSpace'>} [options] - Formatting options
 * @returns {string} Formatted number string
 */
const formatCurrencyValue = (
  value: CurrencyInput,
  options?: Omit<FormatCurrencyOptions, 'showSymbol' | 'useHairSpace'>,
): string => {
  return formatCurrency(value, { ...options, showSymbol: false });
};

/**
 * Formats a currency range (e.g., "$ 100.00 - $ 500.00")
 *
 * @param {CurrencyInput} minValue - The minimum value
 * @param {CurrencyInput} maxValue - The maximum value
 * @param {FormatCurrencyOptions} [options] - Formatting options
 * @returns {string} Formatted currency range string
 */
const formatCurrencyRange = (
  minValue: CurrencyInput,
  maxValue: CurrencyInput,
  options?: FormatCurrencyOptions,
): string => {
  const min = safeParseCurrencyInput(minValue);
  const max = safeParseCurrencyInput(maxValue);

  const fallback = options?.fallback ?? DEFAULT_FALLBACK;

  if (min === null || max === null) {
    return fallback;
  }

  const formattedMin = formatCurrency(min, options);
  const formattedMax = formatCurrency(max, options);

  return `${formattedMin} - ${formattedMax}`;
};

/**
 * Simple axis-tick-style formatter that keeps the currency symbol outside
 * of Intl formatting and mimics the old "$…/k" behavior with a dynamic symbol.
 *
 * - For |value| >= 1000 → "<symbol><value/1000>k"  (e.g., "£1.2k")
 * - For |value| >= 1_000_000 → "<symbol><value/1_000_000>m" (e.g., "£1.0m")
 * - For smaller values  → "<symbol><value>"       (e.g., "£950")
 * @param {CurrencyInput} value - The raw numeric axis value to format.
 * @param {string} symbol - The currency symbol to prefix (e.g., "$", "£", "€").
 */
const formatCurrencyAxisTick = (value: CurrencyInput, symbol: string): string => {
  const parsedValue = safeParseCurrencyInput(value);
  if (parsedValue === null) {
    return DEFAULT_FALLBACK;
  }
  if (Math.abs(parsedValue) >= 1_000_000) {
    return `${symbol}${(parsedValue / 1_000_000).toFixed(1)}m`;
  }
  if (Math.abs(parsedValue) >= 1_000) {
    return `${symbol}${(parsedValue / 1_000).toFixed(1)}k`;
  }

  return `${symbol}${parsedValue}`;
};

/**
 * Formats a currency value with a custom symbol
 *
 * @param {CurrencyInput} value - The value to format
 * @param {string} customSymbol - Custom currency symbol
 * @param {FormatCurrencyOptions} [options] - Formatting options
 * @returns {string} Formatted currency string with custom symbol
 */
const formatCurrencyWithSymbol = (
  value: CurrencyInput,
  customSymbol: string,
  options?: FormatCurrencyOptions,
): string => {
  const parsedValue = safeParseCurrencyInput(value);
  const fallback = options?.fallback ?? DEFAULT_FALLBACK;

  if (parsedValue === null) {
    return fallback;
  }

  // Use default config but override with custom symbol
  const baseConfig = options?.currency
    ? CURRENCY_CONFIGS[options.currency] || DEFAULT_CURRENCY_CONFIG
    : DEFAULT_CURRENCY_CONFIG;
  const config = { ...baseConfig, symbol: customSymbol };

  const finalUseHairSpace = options?.useHairSpace ?? config.useHairSpace;
  const useAccountingFormat = options?.useAccountingFormat ?? false;

  try {
    const isNegative = parsedValue < 0;
    const absoluteValue = Math.abs(parsedValue);

    const formatter = new Intl.NumberFormat(options?.locale || config.locale, {
      style: 'decimal',
      minimumFractionDigits: options?.minimumFractionDigits ?? config.minimumFractionDigits,
      maximumFractionDigits: options?.maximumFractionDigits ?? config.maximumFractionDigits,
      useGrouping: true,
    });

    const formattedNumber = formatter.format(absoluteValue);
    const space = finalUseHairSpace ? HAIR_SPACE : '';

    // Handle accounting format for negative values
    if (useAccountingFormat && isNegative) {
      return config.placement === 'prefix'
        ? `(${customSymbol}${space}${formattedNumber})`
        : `(${formattedNumber}${space}${customSymbol})`;
    }

    // Standard format with placement consideration
    const prefix = isNegative ? '-' : '';

    if (config.placement === 'prefix') {
      return `${prefix}${customSymbol}${space}${formattedNumber}`;
    }

    return `${prefix}${formattedNumber}${space}${customSymbol}`;
  } catch (error) {
    console.warn('Error formatting currency with custom symbol:', error);
    return fallback;
  }
};

export {
  formatCurrency,
  formatCurrencyAxisTick,
  formatCurrencyCompact,
  formatCurrencyRange,
  formatCurrencyValue,
  formatCurrencyWithSymbol,
};
